<script setup lang="ts">
import { computed, onMounted, reactive, ref } from 'vue'
import { useRoute } from 'vue-router'
import PTextarea from 'primevue/textarea'
import { useToast } from 'primevue/usetoast'
import { useVuelidate } from '@vuelidate/core'
import { email, helpers, required } from '@vuelidate/validators'
import { useWindowScroll } from '@vueuse/core'
import { useHead } from '@unhead/vue'
import { useFathom } from '../composables/fathom'
import { subjects } from '../constants/customerServiceSubjects'
import { FirestoreCollection } from '../constants/firestoreCollection'
import { FlowState } from '../constants/flowState'
import FirestoreService from '../services/FirestoreService'
import { delay } from '../utils/delay'

const route = useRoute()
const toast = useToast()

const { y } = useWindowScroll({ behavior: 'smooth' })

const contactForm = reactive({
  name: null as null | string,
  company: null as null | string,
  customerNo: null as null | string,
  email: null as null | string,
  phone: null as null | string,
  phoneExt: null as null | string,
  subject: null as null | string,
  message: '' as string
})

const subjectsArr = Object.values(subjects)
const showSuccess = ref(false)
const flow = ref(FlowState.Idle)

// Regex from https://github.com/vuelidate/vuelidate/pull/708/files
const validUsPhone = helpers.regex(/^\(?(\d{3})\)?[-. ]?(\d{3})[-. ]?(\d{4})$/)

const rules = computed(() => ({
  name: {
    required
  },
  email: {
    required,
    email
  },
  company: {
    required
  },
  phone: {
    required,
    phone: {
      $validator: validUsPhone,
      $message: 'This value is not a valid phone number'
    }
  },
  subject: {
    required
  },
  message: {
    required
  }
}))

const v$ = useVuelidate(rules, contactForm)

function clearForm() {
  contactForm.name = null
  contactForm.company = null
  contactForm.customerNo = null
  contactForm.email = null
  contactForm.phone = null
  contactForm.phoneExt = null
  contactForm.subject = null
  contactForm.message = ''
  v$.value.$reset()
  y.value = 0
}

// TODO
async function submitForm() {
  // Check all fields valid
  v$.value.$validate()

  if (v$.value.$error)
    return
  let msg: string | null = null
  try {
    flow.value = FlowState.Pending
    const res = await FirestoreService.createFirestoreDoc({
      docPath: `${FirestoreCollection.FormSubmissions}`,
      data: {
        ...contactForm,
        created_date: Date.now()
      }
    })

    if (!res.success) {
      msg = 'We apologize. There was an issue sending your email. Please contact your sales rep directly or give us a call.'
      throw new Error(msg)
    }

    await delay(750)
    toast.add({
      detail: 'Thank you for contacting us. A Daughtridge Sales team member will be in contact.',
      life: 4500,
      summary: 'Email Sent',
      severity: 'success'
    })
    y.value = 0
    showSuccess.value = true
    v$.value.$reset()
  }
  catch {
    toast.add({
      detail: msg,
      life: 5000,
      summary: 'Error',
      severity: 'error'
    })
  }
  finally {
    flow.value = FlowState.Idle
  }
}

// HEADER
useHead({
  title: 'Customer Service',
  meta: [
    {
      name: 'description',
      content: 'Need assistance ordering or questions on a part? Contact us.'
    }
  ]
})

const { fathom, FathomEventNames, FathomEvents } = useFathom()
function trackEvent(e: any, payload: any = {}) {
  const events: any = {
    [FathomEventNames.PhoneCall]: () => fathom.trackEvent(FathomEvents.PhoneCall(payload))
  }
  events[e]()
}

onMounted(() => {
  showSuccess.value = false
  if (route.query.subject)
    contactForm.subject = String(route.query.subject)
})
</script>

<template>
  <div
    class="mb-6"
  >
    <h1 class="text-center text-primary-800 m-0">
      Customer Service
    </h1>
  </div>
  <section class="p-2 lg:p-6 surface-section border-round">
    <div class="grid">
      <div class="col-12 md:col-4 lg:col-4">
        <div class="text-2xl text-800 text-center mb-4">
          Need to talk to a Team Member?
        </div>
        <div class="text-center">
          <a
            class="border-round px-3 py-2 text-red-500 border-1 border-transparent hover:border-red-500 text-xl md:text-2xl font-bold text-decoration-none"
            href="tel:+12529771131"
            @click="trackEvent(FathomEventNames.PhoneCall, '12529771131-customer-service')"
          >
            <fa-icon
              icon="fa fa-phone"
              class="hidden sm:inline text-gray-500 text-xl mr-2"
            />
            (252) 977-1131
          </a>
        </div>
        <div class="mt-4 lg:mt-8">
          <span class="block text-xl text-primary">Daughtridge Sales</span>
          <span class="block">600 S Grace St</span>
          <span class="block">PO Box 4364</span>
          <span class="block">Rocky Mount,
            <abbr title="North Carolina">NC</abbr> 27803</span>
        </div>
      </div>
      <div class="col-12 md:col-8 lg:col-6">
        <div class="text-2xl text-primary uppercase mb-4">
          Contact Daughtridge Sales
        </div>
        <div
          v-if="showSuccess"
          class="flex flex-column align-items-center justify-content-center"
        >
          Thank you for contacting us. A Daughtridge Sales team member will be in contact.
          <div class="m-4">
            <router-link
              class="lg:ml-4 router-link border-round hover:bg-blue-100 px-2 py-1 text-primary hover:text-primary-900 text-xl md:text-2xl font-bold text-decoration-none"
              to="/products/base-models"
            >
              <fa-icon
                icon="fa-regular fa-gauge-high"
                class="mr-2"
              />
              Search Products
            </router-link>
          </div>
        </div>
        <div
          v-else
          class="grid"
        >
          <div class="field col-12 md:col-6 py-0 my-0">
            <label
              for="name"
              class="d-headline-sm text-primary-800"
            >
              Name<span class="vertical-align-super text-red-200">*</span>
            </label>
            <p-input-text
              id="name"
              v-model="contactForm.name"
              autocomplete="name"
              class="w-full"
              type="text"
              @blur="v$.name.$touch"
            />
            <div class="h-1rem mb-1">
              <span
                v-if="v$.name?.$error"
                class="text-red-600 text-xs"
              >
                {{ v$.name.$errors[0].$message }}
              </span>
            </div>
          </div>
          <div class="field col-12 md:col-6 py-0 my-0">
            <label
              for="email"
              class="d-headline-sm text-primary-800"
            >
              Email<span class="vertical-align-super text-red-200">*</span>
            </label>
            <p-input-text
              id="email"
              v-model="contactForm.email"
              autocomplete="email"
              class="w-full"
              type="text"
              @blur="v$.email.$touch"
              @input="v$.email.$validate"
            />
            <div class="h-1rem mb-1">
              <span
                v-if="v$.email?.$error"
                class="text-red-600 text-xs"
              >
                {{ v$.email.$errors[0].$message }}
              </span>
            </div>
          </div>
          <div class="field col-12 md:col-6 py-0 my-0">
            <label
              for="company"
              class="d-headline-sm text-primary-800"
            >
              Company<span class="vertical-align-super text-red-200">*</span>
            </label>
            <p-input-text
              id="company"
              v-model="contactForm.company"
              autocomplete="organization"
              class="w-full"
              type="text"
              @blur="v$.company.$touch"
            />
            <div class="h-1rem mb-1">
              <span
                v-if="v$.company?.$error"
                class="text-red-600 text-xs"
              >
                {{ v$.company.$errors[0].$message }}
              </span>
            </div>
          </div>
          <div class="field col-12 md:col-6 py-0 my-0">
            <label
              for="customerNo"
              class="d-headline-sm text-primary-800"
            >
              Customer #
            </label>
            <p-input-text
              id="customerNo"
              v-model="contactForm.customerNo"
              autocomplete="off"
              class="w-full"
              type="text"
            />
          </div>
          <div class="col-12 md:col-6">
            <div class="grid m-0">
              <div class="field col-8 p-0">
                <label
                  for="phone"
                  class="d-headline-sm text-primary-800"
                >
                  Phone<span class="vertical-align-super text-red-200">*</span>
                </label>
                <p-input-text
                  id="phone"
                  v-model="contactForm.phone"
                  autocomplete="tel"
                  class="w-full"
                  @blur="v$.phone.$touch"
                  @input="v$.phone.$touch"
                />
                <div class="h-1rem mb-1">
                  <span
                    v-if="v$.phone?.$error"
                    class="text-red-600 text-xs"
                  >
                    {{ v$.phone.$errors[0].$message }}
                  </span>
                </div>
              </div>
              <div class="field col-4 p-0 pl-3">
                <label
                  for="phoneExt"
                  class="d-headline-sm text-primary-800"
                >
                  Ext
                </label>
                <p-input-text
                  id="phoneExt"
                  v-model="contactForm.phoneExt"
                  autocomplete="off"
                  class="w-full"
                  type="number"
                />
                <div class="h-1rem" />
              </div>
            </div>
          </div>
          <div class="col-12">
            <div class="field">
              <label
                for="subject"
                class="d-headline-sm text-primary-800"
              >
                Subject<span class="vertical-align-super text-red-200">*</span>
              </label>
              <p-dropdown
                id="subject"
                v-model="contactForm.subject"
                aria-label="subject"
                class="w-full"
                :options="subjectsArr"
                @blur="v$.subject.$touch"
              />
              <div class="h-1rem mb-1">
                <span
                  v-if="v$.subject?.$error"
                  class="text-red-600 text-xs"
                >
                  {{ v$.subject.$errors[0].$message }}
                </span>
              </div>
            </div>
          </div>
          <div class="col-12">
            <div class="field">
              <label
                for="message"
                class="d-headline-sm text-primary-800"
              >
                Message<span class="vertical-align-super text-red-200">*</span>
              </label>
              <p-textarea
                id="message"
                v-model="contactForm.message"
                auto-resize
                class="w-full"
                rows="5"
                @blur="v$.message.$touch"
                @input="v$.message.$validate"
              />
              <div class="h-1rem mb-1">
                <span
                  v-if="v$.message?.$error"
                  class="text-red-600 text-xs"
                >
                  {{ v$.message.$errors[0].$message }}
                </span>
              </div>
            </div>
          </div>
          <div class="col-12 text-center md:text-right">
            <p-btn
              class="p-button-text mx-2"
              label="Clear"
              @click="clearForm"
            />
            <p-btn
              :disabled="flow === FlowState.Pending"
              severity="danger"
              @click="submitForm"
            >
              <fa-icon
                v-if="flow === FlowState.Pending"
                icon="fa-solid fa-sync"
                class="hidden sm:inline text-xl mr-2"
                spin
              />
              <fa-icon
                v-else
                icon="fa-solid fa-paper-plane"
                class="hidden sm:inline text-xl mr-2"
              />
              <span>
                Submit
              </span>
            </p-btn>
          </div>
        </div>
        <loader-simple
          v-if="flow === FlowState.Pending"
          bg-color="rgba(0, 0, 0, 0.70)"
          label="Submitting contact form"
          label-color="text-white"
          overlay="full"
        />
      </div>
    </div>
  </section>
</template>
